"use client"

import { Dialog, DialogContent, DialogMenuButton, DialogTrigger } from "@/components"
import { EllipsisHorizontalIcon, FlagIcon, HandRaisedIcon, XMarkIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"
import { usePortfolio } from "../hooks/usePortfolio"
import { toast } from "@/components/Toast"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useMutation } from "@apollo/client"
import { UserConnectionStatus } from "@/gql/graphql"
import { IconButton } from "@/elements"
import { useSession } from "@/hooks/useSession"
import { captureException } from "@sentry/nextjs"
import type { NovaSession } from "@/types"

const ActionsModalFragment = graphql(`
  fragment ActionsModal on User {
    id
    __typename
    uuid
    viewerConnectionStatus
    viewerHasBlocked
  }
`)

interface Props {
  user: FragmentType<typeof ActionsModalFragment> | undefined | null
  isCreative: boolean
  serverSession: NovaSession | null
}

const BlockUserMutation = graphql(`
  mutation BlockUser($userId: ID!) {
    blockUser(userId: $userId) {
      success
      block {
        id
        created
        user: blocked {
          id
          viewerHasBlocked
        }
      }
    }
  }
`)

const UnblockUserMutation = graphql(`
  mutation UnblockUser($userId: ID!) {
    unblockUser(userId: $userId) {
      success
      user {
        id
        viewerHasBlocked
      }
    }
  }
`)

const RemoveConnectionMutation = graphql(`
  mutation RemoveConnection($userId: ID!) {
    removeConnection(userId: $userId) {
      success
      user {
        id
        viewerConnectionStatus
      }
    }
  }
`)

export const ActionsModal: React.FC<Props> = props => {
  const { isCreative, serverSession } = props
  const [blockUser] = useMutation(BlockUserMutation)
  const [unblockUser] = useMutation(UnblockUserMutation)
  const [removeConnection] = useMutation(RemoveConnectionMutation)
  const user = useFragment(ActionsModalFragment, props.user)
  const userId = user?.id
  const [isOpen, setIsOpen] = useState(false)
  const session = useSession()

  const sessionIsCreative = session?.data?.account === "creative"

  const isBlocked = user?.viewerHasBlocked
  const connected = user?.viewerConnectionStatus === UserConnectionStatus.Connected
  const { setShowReportUserModal } = usePortfolio()

  // const onBookmark = () => {}
  const onBlockUser = async () => {
    blockUser({
      variables: {
        userId: userId!,
      },
      onCompleted(data) {
        if (data.blockUser?.success) {
          toast.success("User has been blocked")
        } else {
          toast.error("Error blocking user")
        }
        setIsOpen(false)
      },
      onError(err) {
        captureException(err)
        toast.error("Error blocking user")
      },
    })
  }
  const onUnblockUser = async () => {
    unblockUser({
      variables: {
        userId: userId!,
      },
      onCompleted(data) {
        if (data.unblockUser?.success) {
          toast.success("User has been unblocked")
        } else {
          toast.error("Error unblocking user")
        }
        setIsOpen(false)
      },
      onError(e) {
        captureException(e)
        toast.error("Error unblocking user")
      },
    })
  }
  const onRemoveConnection = () => {
    removeConnection({
      variables: {
        userId: userId!,
      },
      onCompleted(data) {
        if (data.removeConnection?.success) {
          toast.success("Connection has been removed")
        } else {
          toast.error("Error removing connection")
        }
        setIsOpen(false)
      },
      onError(e) {
        captureException(e)
        toast.error("Error removing connection")
      },
    })
  }
  const onReportUser = () => {
    setShowReportUserModal(true)
    setIsOpen(false)
  }
  const onCancel = () => {
    setIsOpen(false)
  }

  const isViewersProfile = user?.id === serverSession?.user?.id

  if (isViewersProfile) {
    return null
  }

  /* does not need converted to ControlledModal */
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div className="flex flex-shrink-0">
          <IconButton Icon={EllipsisHorizontalIcon} onClick={() => setIsOpen(true)} variant="white" />
        </div>
      </DialogTrigger>
      <DialogContent withClose={false} className="w-[400px]" fullScreenOnMobile={false}>
        <div className="w-[400px]! p-2 max-modal-size">
          {/* <DialogMenuButton title="Bookmark" onClick={onBookmark} Icon={BookmarkIcon} />
          <Separator /> */}
          {isCreative && (
            <>
              {isBlocked ? (
                <DialogMenuButton title="Unblock user" onClick={onUnblockUser} Icon={HandRaisedIcon} />
              ) : (
                <DialogMenuButton title="Block user" onClick={onBlockUser} Icon={HandRaisedIcon} color="red" />
              )}
            </>
          )}
          {connected && isCreative && sessionIsCreative && (
            <DialogMenuButton title="Remove connection" onClick={onRemoveConnection} Icon={XMarkIcon} color="red" />
          )}
          {isCreative && <DialogMenuButton title="Report user" onClick={onReportUser} Icon={FlagIcon} color="red" />}
          <DialogMenuButton title="Cancel" onClick={onCancel} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
